import type { ApiClient } from "@/modules/api/apiClient.ts";
import * as queries from "@/modules/billing/paypal/paypalQuery.ts";
import { getPaypalSessionId } from "@/modules/billing/paypal/utils.ts";
import { loadScript } from "@/utils/loadScript.ts";
import type { PayPalNamespace } from "@paypal/paypal-js";
import { notNull } from "@warrenio/utils/notNull";

type SDKComponentsOption = ("buttons" | "messages" | "marks" | "card-fields" | "funding-eligibility")[];
export interface PaypalLoaderProps {
    apiClient: ApiClient;
    signal: AbortSignal;
    clientId: string;
    sdkComponents?: SDKComponentsOption;
}

export async function paypalLoader(
    loaderProps: PaypalLoaderProps,
): Promise<{ paypal: PayPalNamespace; sessionId: string }> {
    // TODO: "paypalSessionId" should be deleted on logout from session storage!
    // Maybe reload window also to remove loaded PayPal JS SDK script?
    const sessionId = getPaypalSessionId();
    {
        // Check if already loaded
        const { paypal } = window;
        if (paypal) {
            return { paypal, sessionId };
        }
    }
    const { apiClient, signal, clientId, sdkComponents } = loaderProps;
    const { token: idToken } = await queries.idTokenQuery(apiClient, signal, sessionId);

    const url = new URL("https://www.paypal.com/sdk/js");
    if (sdkComponents?.length) url.searchParams.append("components", sdkComponents.join(","));
    url.searchParams.append("client-id", clientId);

    await loadScript(url.toString(), (s) => {
        if (idToken) s.setAttribute("data-user-id-token", idToken);
    });

    // Re-get `window.paypal`, otherwise TypeScript narrows it to "undefined" due to the `if (window.paypal)` check above
    const { paypal } = window;
    return { paypal: notNull(paypal, "window.paypal"), sessionId };
}
